import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import {
  History,
  TrendingUp,
  TrendingDown,
  Minus,
  Loader2,
  ChevronDown,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  getGameHistory,
  formatStatsCurrency,
} from '../services/statsService'
import type { GameTransaction } from '@/types'

interface GameHistoryProps {
  userId: string
  isDemo?: boolean
  maxItems?: number
}

export function GameHistory({ userId, isDemo = false, maxItems = 20 }: GameHistoryProps) {
  const { t } = useTranslation()
  const [history, setHistory] = useState<GameTransaction[]>([])
  const [loading, setLoading] = useState(true)
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    loadHistory()
  }, [userId])

  const loadHistory = async () => {
    if (isDemo) {
      setHistory([])
      setLoading(false)
      return
    }

    setLoading(true)
    const transactions = await getGameHistory(userId, undefined, 50)
    setHistory(transactions)
    setLoading(false)
  }

  const displayedHistory = showAll ? history : history.slice(0, maxItems)

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-6 h-6 animate-spin text-casino-gold" />
      </div>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="w-5 h-5 text-casino-gold" />
          {t('stats.recentGames')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {history.length === 0 ? (
          <p className="text-center text-gray-500 py-8">
            {isDemo ? 'Las partidas se registran con cuenta real' : t('stats.noGameHistory')}
          </p>
        ) : (
          <>
            <div className="space-y-2">
              <AnimatePresence>
                {displayedHistory.map((transaction, index) => (
                  <GameTransactionRow
                    key={transaction.id}
                    transaction={transaction}
                    index={index}
                  />
                ))}
              </AnimatePresence>
            </div>

            {history.length > maxItems && (
              <Button
                variant="ghost"
                className="w-full mt-4 text-gray-400 hover:text-white"
                onClick={() => setShowAll(!showAll)}
              >
                <ChevronDown
                  className={`w-4 h-4 mr-2 transition-transform ${showAll ? 'rotate-180' : ''}`}
                />
                {showAll ? 'Ver menos' : `Ver más (${history.length - maxItems} más)`}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

// Transaction Row Component
interface GameTransactionRowProps {
  transaction: GameTransaction
  index: number
}

function GameTransactionRow({ transaction, index }: GameTransactionRowProps) {
  const { t } = useTranslation()

  const getGameLabel = (game: string) => {
    switch (game) {
      case 'ruleta':
        return 'Ruleta'
      case 'blackjack':
        return 'Blackjack'
      case 'baccarat':
        return 'Baccarat'
      default:
        return game
    }
  }

  const getGameColor = (game: string) => {
    switch (game) {
      case 'ruleta':
        return 'bg-red-500/20 text-red-400'
      case 'blackjack':
        return 'bg-green-500/20 text-green-400'
      case 'baccarat':
        return 'bg-purple-500/20 text-purple-400'
      default:
        return 'bg-gray-500/20 text-gray-400'
    }
  }

  const getResultIcon = () => {
    if (transaction.netResult > 0) {
      return <TrendingUp className="w-4 h-4 text-green-400" />
    } else if (transaction.netResult < 0) {
      return <TrendingDown className="w-4 h-4 text-red-400" />
    }
    return <Minus className="w-4 h-4 text-gray-400" />
  }

  const getResultLabel = () => {
    if (transaction.netResult > 0) {
      return t('stats.win')
    } else if (transaction.netResult < 0) {
      return t('stats.loss')
    }
    return t('stats.push')
  }

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('es-AR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date)
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ delay: index * 0.05 }}
      className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg hover:bg-zinc-800/50 transition-colors"
    >
      <div className="flex items-center gap-3">
        <span className={`px-2 py-1 rounded text-xs font-medium ${getGameColor(transaction.game)}`}>
          {getGameLabel(transaction.game)}
        </span>
        <div>
          <div className="flex items-center gap-2">
            {getResultIcon()}
            <span className="text-sm text-white">{getResultLabel()}</span>
          </div>
          <p className="text-xs text-gray-500">{formatDate(transaction.createdAt)}</p>
        </div>
      </div>

      <div className="text-right">
        <p className="text-xs text-gray-400">
          {t('stats.bet')}: {formatStatsCurrency(transaction.amount)}
        </p>
        <p
          className={`text-sm font-bold ${
            transaction.netResult > 0
              ? 'text-green-400'
              : transaction.netResult < 0
              ? 'text-red-400'
              : 'text-gray-400'
          }`}
        >
          {transaction.netResult > 0 ? '+' : ''}
          {formatStatsCurrency(transaction.netResult)}
        </p>
      </div>
    </motion.div>
  )
}

export default GameHistory
